import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useToast } from './ToastContainer';

interface GuestLink {
  id: string;
  token: string;
  createdBy: string;
  createdAt: string;
  expiresAt: string;
  description?: string;
  guestUrl: string;
  timeRemaining: number;
  isExpired: boolean;
  guestName?: string;
  quizId?: string;
  quizTitle?: string;
}

interface Quiz {
  id: string;
  title: string;
  description?: string;
  category?: string;
}

const GuestLinkManager: React.FC = () => {
  const { showToast } = useToast();
  const [guestLinks, setGuestLinks] = useState<GuestLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [description, setDescription] = useState('');
  const [expiryMinutes, setExpiryMinutes] = useState('30');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [selectedQuizId, setSelectedQuizId] = useState<string>('');
  const [loadingQuizzes, setLoadingQuizzes] = useState(false);

  useEffect(() => {
    fetchGuestLinks();
    fetchQuizzes();
    // Set up auto-refresh every 30 seconds
    const interval = setInterval(fetchGuestLinks, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchGuestLinks = async () => {
    try {
      const response = await api.get('/api/guest-links/active');
      setGuestLinks(response.data.data);
      setLoading(false);
    } catch (error: any) {
      console.error('Error fetching guest links:', error);
      const errorMessage = error.response?.data?.message || 'Failed to fetch guest links';
      showToast(errorMessage, 'error');
      setLoading(false);
    }
  };

  const fetchQuizzes = async () => {
    setLoadingQuizzes(true);
    try {
      const response = await api.get('/api/quizzes');
      setQuizzes(response.data);
    } catch (error: any) {
      console.error('Error fetching quizzes:', error);
      showToast('Failed to fetch quizzes', 'error');
    } finally {
      setLoadingQuizzes(false);
    }
  };

  const createGuestLink = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (creating) return;
    
    setCreating(true);
    
    try {
      const minutes = parseInt(expiryMinutes);
      if (isNaN(minutes) || minutes <= 0 || minutes > 10080) {
        showToast('Expiry minutes must be between 1 and 10080 (1 week)', 'error');
        setCreating(false);
        return;
      }

      const response = await api.post('/api/guest-links/create', {
        description: description.trim() || undefined,
        expiryMinutes: minutes,
        quizId: selectedQuizId ? Number(selectedQuizId) : undefined
      });
      
      const newLink = response.data.data;
      setGuestLinks(prev => [newLink, ...prev]);
      setDescription('');
      setExpiryMinutes('30');
      setSelectedQuizId('');
      setShowCreateForm(false);
      
      showToast('Guest link created successfully!', 'success');
      
      // Copy to clipboard
      await navigator.clipboard.writeText(newLink.guestUrl);
      showToast('Guest link copied to clipboard!', 'info');
      
    } catch (error: any) {
      console.error('Error creating guest link:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create guest link';
      showToast(errorMessage, 'error');
    } finally {
      setCreating(false);
    }
  };

  const revokeGuestLink = async (linkId: string, linkDescription?: string) => {
    const displayName = linkDescription || 'this guest link';
    if (!window.confirm(`Are you sure you want to revoke ${displayName}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await api.delete(`/api/guest-links/${linkId}`);
      setGuestLinks(prev => prev.filter(link => link.id !== linkId));
      showToast('Guest link revoked successfully!', 'success');
    } catch (error: any) {
      console.error('Error revoking guest link:', error);
      const errorMessage = error.response?.data?.message || 'Failed to revoke guest link';
      showToast(errorMessage, 'error');
    }
  };

  const copyToClipboard = async (text: string, linkId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedLinkId(linkId);
      setTimeout(() => setCopiedLinkId(null), 2000);
      showToast('Copied to clipboard!', 'success');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      showToast('Failed to copy to clipboard', 'error');
    }
  };

  const formatTimeRemaining = (milliseconds: number) => {
    if (!milliseconds || milliseconds <= 0) return 'Expired';
    
    const minutes = Math.floor(milliseconds / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${remainingMinutes}m remaining`;
    } else if (minutes > 0) {
      return `${minutes}m remaining`;
    } else {
      return 'Less than 1m remaining';
    }
  };
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="guest-link-manager">
        <div className="card text-center">
          <div className="loading-spinner"></div>
          <p>Loading guest links...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="guest-link-manager">
      <div className="card mb-3">
        <div className="section-header-row">
          <h2 className="section-title">Guest Link Management</h2>
          <button 
            onClick={() => setShowCreateForm(true)}
            className="btn btn-primary"
            disabled={creating}
          >
            Create Guest Link
          </button>
        </div>
        <p className="description">
          Create temporary access links for online interviews. Each link can have its own expiry duration.
        </p>

        {/* Guest Link Statistics */}
        <div className="guest-link-stats">
          <div className="stat-bubble">
            <span className="stat-name">Active Links</span>
            <span className="stat-count">{guestLinks.filter(l => !l.isExpired).length}</span>
          </div>
          <div className="stat-bubble">
            <span className="stat-name">Expired Links</span>
            <span className="stat-count">{guestLinks.filter(l => l.isExpired).length}</span>
          </div>
          <div className="stat-bubble">
            <span className="stat-name">Longest Time Left (mins)</span>
            <span className="stat-count">{
              guestLinks.length > 0 
                ? (Math.max(
                    ...guestLinks
                      .filter(l => !l.isExpired)
                      .map(link => Math.ceil(link.timeRemaining / (60 * 1000)))
                      .filter(n => n > 0)
                  ) || 0)
                : 0
            }</span>
          </div>
        </div>
      </div>

      {showCreateForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Create New Guest Link</h3>
            <form onSubmit={createGuestLink}>
              <div className="form-group">
                <label htmlFor="description">Description (optional)</label>
                <input
                  type="text"
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g., Interview with John Doe"
                  maxLength={100}
                  className="form-control"
                  autoFocus
                />
                <small className="form-hint">Add a description to identify this link later</small>
              </div>
              <div className="form-group">
                <label htmlFor="expiryMinutes">Expiry Time (minutes) *</label>
                <input
                  type="number"
                  id="expiryMinutes"
                  value={expiryMinutes}
                  onChange={(e) => setExpiryMinutes(e.target.value)}
                  min="1"
                  max="10080"
                  required
                  className="form-control"
                />
                <small className="form-hint">Enter minutes between 1 and 10080 (1 week). Default: 30 minutes</small>
              </div>
              <div className="form-group">
                <label htmlFor="quizId">Restrict to Quiz (optional)</label>
                <select
                  id="quizId"
                  value={selectedQuizId}
                  onChange={(e) => setSelectedQuizId(e.target.value)}
                  className="form-control"
                  disabled={loadingQuizzes}
                >
                  <option value="">All Quizzes (No Restriction)</option>
                  {quizzes.map(quiz => (
                    <option key={quiz.id} value={quiz.id}>
                      {quiz.title} {quiz.category ? `(${quiz.category})` : ''}
                    </option>
                  ))}
                </select>
                <small className="form-hint">
                  Select a specific quiz to restrict guest access, or leave empty to allow access to all quizzes
                </small>
              </div>
              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={() => {
                    setShowCreateForm(false);
                    setDescription('');
                    setExpiryMinutes('30');
                    setSelectedQuizId('');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" disabled={creating} className="btn btn-primary">
                  {creating ? 'Creating...' : 'Create Link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="guest-links-list">
        {guestLinks.length === 0 ? (
          <div className="card text-center">
            <h3>No Guest Links Yet</h3>
            <p>Create your first guest link to allow temporary access for online interviews.</p>
            <button 
              onClick={() => setShowCreateForm(true)}
              className="btn btn-primary btn-large mt-2"
            >
              Create Your First Guest Link
            </button>
          </div>
        ) : (
          <div className="links-grid">
            {guestLinks.map(link => (
              <div key={link.id} className={`card link-card ${link.isExpired ? 'expired' : ''} shadow-hover`}>
                <div className="link-header">
                  <div className="link-info">
                    <h3 className="link-title">
                      {link.description || 'Guest Link'}
                    </h3>
                    <div className="link-meta">
                      <span className="meta-item">
                        {link.createdBy}
                      </span>
                      <span className="meta-item">
                        {formatDate(link.createdAt)}
                      </span>
                      {link.quizTitle && (
                        <span className="meta-item" style={{ color: '#007bff', fontWeight: 'bold' }}>
                          Quiz: {link.quizTitle}
                        </span>
                      )}
                      {!link.quizId && (
                        <span className="meta-item" style={{ color: '#28a745' }}>
                          All Quizzes
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="link-status">
                    <span className={`status-badge ${link.isExpired ? 'status-expired' : 'status-active'}`}>
                      {link.isExpired ? 'Expired' : 'Active'}
                    </span>
                    {!link.isExpired && (
                      <span className="time-remaining">
                        {formatTimeRemaining(link.timeRemaining)}
                      </span>
                    )}
                    {link.guestName && (
                      <span className="guest-name">
                        {link.guestName}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="link-url-section">
                  <label className="url-label">Guest Access URL:</label>
                  <div className="link-url">
                    <input
                      type="text"
                      value={link.guestUrl}
                      readOnly
                      onClick={(e) => (e.target as HTMLInputElement).select()}
                      className="form-control"
                    />
                    <button
                      onClick={() => copyToClipboard(link.guestUrl, link.id)}
                      className={`btn ${copiedLinkId === link.id ? 'btn-success' : 'btn-outline'} btn-small`}
                      title="Copy to clipboard"
                    >
                      {copiedLinkId === link.id ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </div>
                
                <div className="link-actions">
                  <button
                    onClick={() => window.open(link.guestUrl, '_blank')}
                    className="btn btn-info btn-small"
                    title="Preview link"
                  >
                    Preview
                  </button>
                  <button
                    onClick={() => revokeGuestLink(link.id, link.description)}
                    className="btn btn-danger btn-small"
                    disabled={link.isExpired}
                  >
                    {link.isExpired ? 'Expired' : 'Revoke'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GuestLinkManager; 